import { forwardRef } from "react";
import { graphql, useFragment } from "react-relay";

import type { ContentItemRenderVariantProps } from "~/components/content/content-item-render/types";
import type { primaryContentItemContent$key } from "~/queries/__generated__/primaryContentItemContent.graphql";

import {
  Container,
  Content,
  Cover,
  Footer,
  Header,
  LinkContainer,
  LiveTagContainer,
  StyledEntityLink,
} from "./styles";

export type Props = {
  reference: primaryContentItemContent$key;
} & ContentItemRenderVariantProps;

export const ContentItemPrimary = forwardRef<HTMLDivElement, Props>(
  ({ className, provider, reference: reference_ }, reference) => {
    const entity = useFragment(
      graphql`
        fragment primaryContentItemContent on Content {
          ...entityLink
        }
      `,
      reference_,
    );

    const header = (
      <>
        <LiveTagContainer>{provider.liveTag()}</LiveTagContainer>
        <LinkContainer>
          {provider.authorLink?.()}
          {provider.topicLink?.()}
        </LinkContainer>
        <StyledEntityLink reference={entity}>
          {provider.headline({
            hideTooltip: true,
            preferSocialHeadline: true,
          })}
        </StyledEntityLink>
      </>
    );
    const cover = (
      <StyledEntityLink reference={entity}>
        {provider.coverImage({
          responsiveVariants: {
            desktopUp: "size1200x800",
            mobileUp: "size540x360",
            tabletUp: "size540x360",
          },
        })}
        {provider.video?.durationOverlay({
          responsiveVariants: { desktopUp: "large", mobileUp: "large", tabletUp: "large" },
        })}
      </StyledEntityLink>
    );
    const content = provider.video ? null : provider.subHeadline();
    const footer =
      provider.sponsorTag?.({
        color: "#001246",
        gap: "4px",
        layoutDirection: "horizontal",
        nameFontSize: "14px",
        typeFontSize: "14px",
      }) ?? provider.actionBar();

    return (
      <Container className={className} ref={reference}>
        <Header>{header}</Header>
        <Cover>{cover}</Cover>
        <Content>{content}</Content>
        <Footer>{footer}</Footer>
      </Container>
    );
  },
);

ContentItemPrimary.displayName = "ContentItemPrimary";
